import { Col, Divider, Layout, Modal, Pagination, Row, Spin, Button } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import Categories from '../../components/Query/Categories';
import GetFullVariants from '../../components/Query/Products/GetFullVariants';
import SearchCatalog from '../../components/Query/Products/SearchCatalog';
import { filterAllAttributesFromCategories } from '../../helpers/Categories/filterAllAttributesFromCategories';
import ProductDetail from '../Products/ProductDetail';
import SideMenu from './components/SideMenu';
import { VariantCard } from './components/VariantCard';

import './style.scss';
import { getPrivilegesTo } from 'src/helpers/Users/getPrivilegesTo';
import { privileges } from 'data-layer/src/services/Users/privileges';

export interface ISelectedAttributeValues {
  [id: string]: Array<string>;
}

const { Sider } = Layout;

interface ICatalogState {
  searchQuery: string;
  categoryId: string | undefined | null;
  selectedAttributeValues: ISelectedAttributeValues;
  offset: number;
  limit: number;
  editProductId: number | string | undefined;
  editParentCategoryId: number | string | undefined;
  enableDragAndDrop: boolean;
  displayAll: boolean;
  productPriorities: any;
}

interface ICatalogProps extends RouteComponentProps<{ eshop: string; categoryId: string }> {
  onCollapse?: any;
  currency: GQL.ICurrency;
}

class Catalog extends React.Component<ICatalogProps, ICatalogState> {

  constructor(props: any) {
    super(props);

    this.state = {
      searchQuery: '',
      categoryId: null,
      selectedAttributeValues: {},
      offset: 0,
      limit: 80,
      editProductId: undefined,
      editParentCategoryId: undefined,
      enableDragAndDrop: false,
      displayAll: false,
      productPriorities: {},
    };

    this.changeSelectedAttributes = this.changeSelectedAttributes.bind(this);
    this.onDragChange = this.onDragChange.bind(this);
    this.toggleDisplayAll = this.toggleDisplayAll.bind(this);
    this.onChangeProductPriorities = this.onChangeProductPriorities.bind(this);
  }

  async componentDidMount() {
    if (this.props.onCollapse) {
      this.props.onCollapse(true);
    }
  }

  componentDidUpdate() {
    const { categoryId } = this.props.match.params;

    // reset
    if (!categoryId && this.state.categoryId) {
      this.setState({ categoryId: undefined });
    }

    // set a new one
    if (categoryId && this.state.categoryId !== categoryId) {
      this.setState({
        categoryId: categoryId ? categoryId : null,
      });
    }
  }

  onDragChange(enableDragAndDrop) {
    if (getPrivilegesTo(privileges.EDITING_PRODUCTS)) {
      this.setState({
        enableDragAndDrop
      });
    }
  }

  toggleDisplayAll() {
    this.setState({
      displayAll: !this.state.displayAll,
    });
  }

  /**
   * Aktualizují se nové priority do katalogu
   * 
   * @param categoryId 
   * @param productPriorities 
   */
  onChangeProductPriorities(categoryId: string, productPriorities: any[]) {
    const newProductPriorities = { ...this.state.productPriorities, [categoryId]: productPriorities }
    this.setState({
      productPriorities: newProductPriorities,
    });
  }

  render() {
    const domain = this.props.match.params.eshop;
    let filterProducts: any = memoizeFilterProducts(this.filterVariants.bind(this));

    const canDragAndDrop = !!this.state.categoryId && getPrivilegesTo(privileges.EDITING_PRODUCTS);

    const { currency } = this.props;

    return (
      <Layout>
        <Categories domain={this.props.match.params.eshop}>
          {({ categories }: any) => {
            if (!categories.categories || categories.loading) {
              return (
                <div style={{ padding: 10 }}>
                  <Spin /> Loading categories...
                </div>
              );
            }
            return [
              <Sider key={'1'} style={{ background: '#fff', paddingRight: 16, borderRight: '1px solid #e8e8e8' }}>
                <SideMenu
                  onSearch={q => this.setState({
                    offset: 0,
                    searchQuery: q
                  })}
                  onChange={key => this.changeCategory(key)}
                  categories={categories.categories}
                />
              </Sider>,
              <SearchCatalog
                key={'3'}
                domain={domain}
                categoryId={this.state.categoryId}
                search={this.state.searchQuery}
              >
                {({ minimalProducts, productsCategories, deleteProduct, updateProductPriority }: any) => {
                  if (!minimalProducts.minimalProducts || minimalProducts.loading || productsCategories.loading) {
                    filterProducts = undefined;

                    return (
                      <div style={{ padding: 10 }}>
                        <Spin /> Loading products & attributes...
                      </div>
                    );
                  }

                  if (filterProducts == undefined) {
                    filterProducts = memoizeFilterProducts(this.filterVariants.bind(this));
                  }

                  // seznam produktů, minimum informací
                  const products = minimalProducts.minimalProducts as Array<GQL.IProduct>;

                  // aktuální kategorie
                  const category = categories.categories.find(({ id }: GQL.ICategory) => {
                    return id === this.state.categoryId;
                  });

                  const priority = {};

                  // virtuální priority (ty co jsou vráceny ze změny pořadí produktů)
                  if (this.state.categoryId && this.state.productPriorities[this.state.categoryId]) {
                    this.state.productPriorities[this.state.categoryId].forEach((rec: any) => {
                      if (rec && rec.productId) {
                        priority[rec.productId] = rec.priority;
                      }
                    });
                  } else {
                    // seznam priorit z backendu (z db)
                    productsCategories && productsCategories.productsCategories && productsCategories.productsCategories.forEach((rec: any) => {
                      if (rec && rec.productId) {
                        priority[rec.productId] = rec.priority;
                      }
                    });
                  }

                  let attributesForHeader = new Array<GQL.IAttribute>();
                  if (category) {
                    attributesForHeader = filterAllAttributesFromCategories([category]);
                  } else {
                    attributesForHeader = filterAllAttributesFromCategories(categories.categories);
                  }

                  const filteredVariants = filterProducts(products);
                  let paginatedVariants = filteredVariants
                    .sort((a: GQL.IProductVariant, b: GQL.IProductVariant) => {
                      if (!priority[a.productId]) {
                        return 1;
                      }
                      if (!priority[b.productId]) {
                        return -1;
                      }
                      if (priority && priority[a.productId] && priority[b.productId]) {
                        return (priority[a.productId] < priority[b.productId] ? -1 : 0);
                      }
                      return 0;
                    });

                  if (!this.state.displayAll) {
                    paginatedVariants = paginatedVariants
                      .slice(
                        this.state.offset,
                        this.state.offset + this.state.limit
                      ) as Array<GQL.IProductVariant>;
                  }

                  const idsToFetch = paginatedVariants
                    .map(v => v.id)
                    .sort((a, b) => a < b ? -1 : 1);

                  return (
                    <Layout key={'2'}>
                      <Layout.Content style={{ background: '#fff', paddingLeft: 16 }}>
                        <GetFullVariants returnEmpty={idsToFetch.length == 0} ids={idsToFetch}>
                          {({ fullVariants }: any) => {
                            const { productTags } = fullVariants;

                            return (
                              <Layout>
                                <Row style={{ background: '#fff' }}>
                                  <Button
                                    type={this.state.displayAll ? 'primary' : 'default'}
                                    style={{ marginLeft: '15px' }}
                                    onClick={this.toggleDisplayAll}
                                  >
                                    {this.state.displayAll &&
                                      <CheckOutlined />}
                                    Display all products
                                  </Button>

                                  <Button
                                    type={this.state.enableDragAndDrop ? 'primary' : 'default'}
                                    style={{ marginLeft: '15px' }}
                                    disabled={!canDragAndDrop}
                                    onClick={() => this.onDragChange(!this.state.enableDragAndDrop)}
                                  >
                                    {this.state.enableDragAndDrop &&
                                      <CheckOutlined />}
                                    Enable Drag & Drop
                                  </Button>

                                  {!this.state.displayAll &&
                                    <Pagination
                                      size="small"
                                      pageSize={this.state.limit}
                                      current={
                                        this.state.offset / this.state.limit + 1 ||
                                        1
                                      }
                                      total={filteredVariants.length}
                                      onChange={page => {
                                        this.setState({
                                          offset: this.state.limit * (page - 1)
                                        });
                                      }}
                                      showTotal={total =>
                                        `Total ${total} product variants`
                                      }
                                      defaultCurrent={1}
                                      style={{
                                        marginTop: '4px',
                                        marginLeft: 'auto'
                                      }}
                                    />}

                                  <div
                                    style={{
                                      clear: 'both'
                                    }}
                                  />
                                  <Divider />

                                  {!fullVariants.loading &&
                                    (fullVariants.variants as Array<GQL.IProductVariant>)
                                      .sort((a: GQL.IProductVariant, b: GQL.IProductVariant) => {
                                        if (!priority[a.productId]) {
                                          return 1;
                                        }
                                        if (!priority[b.productId]) {
                                          return -1;
                                        }
                                        if (priority && priority[a.productId] && priority[b.productId]) {
                                          return (priority[a.productId] < priority[b.productId] ? -1 : 0);
                                        }
                                        return 0;
                                      })
                                      .map(v => {
                                        return (
                                          <Col lg={4} md={8} sm={12} xs={24} key={v.id}>
                                            <VariantCard
                                              onDragChange={this.onDragChange}
                                              enableDragAndDrop={this.state.enableDragAndDrop && canDragAndDrop}
                                              category={category}
                                              deleteProduct={() =>
                                                this.deleteProduct(
                                                  v.product,
                                                  deleteProduct,
                                                  minimalProducts.refetch
                                                )
                                              }
                                              setCurrentProduct={() =>
                                                this.editProduct(v.product)
                                              }
                                              product={v.product}
                                              variant={v}
                                              tags={productTags}
                                              currency={currency}
                                              priority={priority && v.product && priority[v.productId]
                                                ? priority[v.productId]
                                                : null}
                                              onChangePriority={async (productId: string, priority: number) => {
                                                const productPriorities = await updateProductPriority({
                                                  variables: {
                                                    productId: productId,
                                                    categoryId: category.id,
                                                    priority: priority,
                                                  }
                                                });
                                                if (productPriorities && productPriorities.data && productPriorities.data.updateProductPriority) {
                                                  this.onChangeProductPriorities(category.id, productPriorities.data.updateProductPriority);
                                                }
                                              }}
                                            />
                                          </Col>
                                        );
                                      })}
                                  {fullVariants.loading &&
                                    <div>
                                      <Spin /> Loading product cards
                                    </div>}
                                </Row>

                                {this.state.editProductId && (
                                  <ProductDetail
                                    domain={domain}
                                    productId={this.state.editProductId}
                                    onSave={() => {
                                      if (fullVariants.refetch) {
                                        fullVariants.refetch();
                                      }
                                      if (minimalProducts.refetch) {
                                        minimalProducts.refetch();
                                      }
                                      this.setState({
                                        editProductId: undefined
                                      });
                                    }}
                                    onCancel={() =>
                                      this.setState({
                                        editProductId: undefined
                                      })
                                    }
                                  />
                                )}
                              </Layout>
                            );
                          }}
                        </GetFullVariants>
                      </Layout.Content>
                    </Layout>
                  );
                }}
              </SearchCatalog>
            ];
          }}
        </Categories>
      </Layout>
    );
  }

  private editProduct(product: GQL.IProduct) {
    if (getPrivilegesTo(privileges.EDITING_PRODUCTS)) {
      this.setState({
        editProductId: product.id
      });
    }
  }

  private deleteProduct(product: GQL.IProduct, deleteProduct: Function, refetch: Function) {
    if (getPrivilegesTo(privileges.DELETING_PRODUCTS)) {
      Modal.confirm({
        title: `Delete ${product.title}?`,
        content: (
          <span>
            Are you sure you want to delete product <strong>{product.title}</strong>?
          </span>
        ),
        onOk: () => {
          return deleteProduct({
            variables: {
              id: product.id
            }
          }).then(() => refetch());
        }
      });
    }
  }

  private changeSelectedAttributes(valueIds: Array<string>, attribute: GQL.IAttribute) {
    const selectedAttributeValues = {
      ...this.state.selectedAttributeValues,
      [attribute.id]: valueIds
    };

    if (valueIds.length === 0) {
      delete selectedAttributeValues[attribute.id];
    }

    this.setState({ offset: 0, selectedAttributeValues });
  }

  private filterVariants(variants: Array<GQL.IProductVariant>): Array<GQL.IProductVariant> {
    const filtered = new Array<GQL.IProductVariant>();

    for (const variant of variants) {
      let valid = true;
      for (const aId in this.state.selectedAttributeValues) {
        if ((variant.definition || {})[aId] == undefined) {
          valid = false;
          break;
        }

        if (Array.isArray(variant.definition[aId])) {
          let tempValid = false;
          for (const id of variant.definition[aId]) {
            if (this.state.selectedAttributeValues[aId].some(v => v == id)) {
              tempValid = true;
              break;
            }
          }

          valid = tempValid;
        } else if (
          !this.state.selectedAttributeValues[aId].some(v => v == variant.definition[aId]) // nebo pokud ve vybranych nenajdu
        ) {
          valid = false;
          break;
        }
      }

      if (valid) {
        filtered.push(variant);
      }
    }

    return filtered;
  }

  private changeCategory(categoryId: string) {
    if (categoryId !== 'catalog-root') {
      this.setState({ categoryId });
    } else {
      if (this.state.enableDragAndDrop) {
        this.setState({ enableDragAndDrop: false });
      }
    }
  }
}

const memoizeFilterProducts = (variantsFilter: (variants: Array<GQL.IProductVariant>) => Array<GQL.IProductVariant>) => {
  let cache = new Array<GQL.IProductVariant>();

  return (products: Array<GQL.IProduct>) => {
    if (cache.length == 0) {
      cache = filterProducts(products, variantsFilter);
      return cache;
    } else {
      return cache;
    }
  };
};

const filterProducts = (
  products: Array<GQL.IProduct>,
  variantsFilter: (variants: Array<GQL.IProductVariant>) => Array<GQL.IProductVariant>
): Array<GQL.IProductVariant> => {
  const filtered: { [productId: string]: { [valueId: string]: GQL.IProductVariant } } = {};

  for (const product of products) {
    if (filtered[product.id] == undefined) {
      filtered[product.id] = {};
    }

    for (const variant of variantsFilter(product.variants || [])) {
      const visualValueId = (variant.definition || {}).visual;
      if (visualValueId == undefined) {
        if (!Array.isArray(filtered[product.id]['-1'])) {
          filtered[product.id]['-1'] = variant;
        }
      } else {
        if (Array.isArray(visualValueId)) {
          filtered[product.id][visualValueId.join('-')] = variant;
        } else {
          filtered[product.id][visualValueId] = variant;
        }
      }
    }
  }

  const variants = new Array<GQL.IProductVariant>();
  for (const product of Object.keys(filtered)) {
    for (const valueId of Object.keys(filtered[product])) {
      variants.push(filtered[product][valueId]);
    }
  }

  return variants;
};

export default withRouter(Catalog);



// WEBPACK FOOTER //
// ./src/scenes/Catalog/Catalog.tsx